import PageHero from "@/components/layout/PageHero";
import Breadcrumbs from "@/components/layout/Breadcrumbs";

export function LegalPage({ title, kicker, lastUpdated, sections }) {
  return (
    <div data-testid={`legal-${title.toLowerCase().replace(/\s+/g, "-")}`}>
      <PageHero kicker={kicker} title={title} subtitle={`Last updated: ${lastUpdated}`} />
      <div className="container-page py-10 max-w-3xl">
        <Breadcrumbs items={[{ label: title }]} />
        <div className="space-y-8 text-slate-700 leading-relaxed">
          {sections.map((s) => (
            <section key={s.title}>
              <h2 className="font-montserrat font-bold text-xl text-slate-900 mb-2">{s.title}</h2>
              <p className="whitespace-pre-line">{s.body}</p>
            </section>
          ))}
        </div>
      </div>
    </div>
  );
}

export function Privacy() {
  return (
    <LegalPage
      kicker="Legal"
      title="Privacy Policy"
      lastUpdated="January 2026"
      sections={[
        { title: "Introduction", body: "MyTripPoint ('we', 'us') respects your privacy. This policy explains what data we collect, how we use it, and your rights." },
        { title: "Information we collect", body: "Account data (name, email, hashed password), trip search history, newsletter subscription email, contact-form messages and analytics about how you use the site." },
        { title: "How we use data", body: "To provide booking comparison, send weekly deal newsletters (only if you opt in), improve user experience, and prevent fraud. We never sell your personal data." },
        { title: "Cookies", body: "We use httpOnly session cookies for authentication and minimal first-party analytics cookies. You can clear or block cookies via your browser." },
        { title: "Affiliate tracking", body: "When you click partner links (Booking.com, Agoda, Skyscanner, etc.) the partner may set cookies. Please see their respective privacy policies." },
        { title: "Your rights", body: "You may request access, correction or deletion of your data at any time by emailing hello@mytrippoint.com." },
      ]}
    />
  );
}

export function Terms() {
  return (
    <LegalPage
      kicker="Legal"
      title="Terms and Conditions"
      lastUpdated="January 2026"
      sections={[
        { title: "Acceptance", body: "By using MyTripPoint you agree to these terms. If you don't agree, please stop using the site." },
        { title: "Service nature", body: "MyTripPoint is a travel-comparison and affiliate platform. We do not directly sell flights, hotels or packages — we link you to trusted third-party partners who do." },
        { title: "Pricing accuracy", body: "Prices, availability and partner offers change rapidly. We work hard to keep listings accurate but final pricing is always determined at checkout on the partner site." },
        { title: "User accounts", body: "You are responsible for keeping your login credentials secure. Notify us immediately of any unauthorised access to your account." },
        { title: "Limitation of liability", body: "MyTripPoint is not liable for travel arrangements completed through third-party partners. Disputes related to bookings should be raised with the partner." },
        { title: "Changes", body: "We may update these terms. Continued use of the site after updates means you accept the revised terms." },
      ]}
    />
  );
}

export function AffiliateDisclosure() {
  return (
    <LegalPage
      kicker="Legal"
      title="Affiliate Disclosure"
      lastUpdated="January 2026"
      sections={[
        { title: "How we earn", body: "MyTripPoint operates on an affiliate revenue model. When you click certain links on this site — for example to Booking.com, Agoda, MakeMyTrip, Skyscanner or Tripadvisor — and complete a booking, we may earn a small commission from the partner." },
        { title: "Your price stays the same", body: "Affiliate commissions are paid by the partner out of their own margin. The price you see and pay on the partner site is not affected by our affiliate relationship." },
        { title: "Editorial independence", body: "We do not let commercial relationships influence which deals or partners we recommend. Reviews and rankings reflect honest opinions of our editorial team." },
        { title: "Identifying affiliate links", body: "Affiliate links on this site are marked rel='sponsored' in line with industry best practice. The footer of every page also links to this disclosure." },
      ]}
    />
  );
}

export function NotFound() {
  return (
    <div data-testid="not-found-page" className="container-page py-24 text-center">
      <div className="text-7xl font-montserrat font-extrabold text-brand">404</div>
      <div className="mt-3 text-2xl font-montserrat font-bold text-slate-900">Lost your way?</div>
      <p className="mt-2 text-slate-600">The page you're looking for isn't here. Let's get you home.</p>
      <a href="/" className="inline-block mt-6 px-6 h-12 leading-[3rem] rounded-lg btn-brand text-white font-semibold">Back to home</a>
    </div>
  );
}
